/**
 * @file pages/Login.jsx
 * @description Login page with email/password and Google OAuth sign-in.
 * Integrates Firebase auth with backend JWT token generation and redirect handling.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { FaGoogle, FaEnvelope, FaSpinner } from 'react-icons/fa';
import AnimatedButton from '../Components/Ui/AnimatedButton';
import { toast } from 'react-toastify';

import { auth } from '../config/firebase';
import { login, firebaseLogin, getCurrentUser } from '../services/api';

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider();

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEmailLogin, setIsEmailLogin] = useState(false);
  const emailInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle successful login
  const handleLoginSuccess = (userData, token) => {
    // Store user data in localStorage
    const userToStore = { ...userData, token };
    localStorage.setItem('user', JSON.stringify(userToStore));
    localStorage.setItem('token', token);

    // Show success message
    toast.success('Successfully logged in!');
    
    // Get redirect path from location state or query params
    const from = location.state?.from?.pathname || 
                new URLSearchParams(location.search).get('redirect') || 
                null;
    
    // If no specific redirect, send admins to /admin, others to /
    const defaultPath = (userData.isAdmin || userData.role === 'admin') ? '/admin' : '/';
    
    // Navigate to the intended destination
    navigate(from || defaultPath, { 
      replace: true,
      state: { from: undefined }
    });
  };

  // Handle email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setLoading(true);
      
      // Send email + password directly to backend
      const response = await login({ email, password });
      const token = response.data?.token;
      const userData = response.data?.data?.user;

      if (token && userData) {
        handleLoginSuccess(userData, token);
      } else {
        setError('Login failed: invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to sign in. Please check your credentials.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google login
  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Sign in with Google via Firebase
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      
      // Verify with backend via Firebase login endpoint
      const response = await firebaseLogin({
        email: result.user.email,
        firebaseToken: idToken,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });
      
      const token = response.data?.token;
      const userData = response.data?.data?.user;
      
      if (token && userData) {
        handleLoginSuccess(userData, token);
      } else {
        setError('Login failed: invalid response from server');
      }
    } catch (error) {
      console.error('Google login error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Google login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if already logged in on mount and redirect
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser.token && storedUser.email) {
      const defaultPath = (storedUser.isAdmin || storedUser.role === 'admin') ? '/admin' : '/';
      const from = location.state?.from?.pathname || defaultPath;
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  const goToSignup = () => navigate('/signup');
  const goToForgotPassword = () => navigate('/forgot-password');
  const toggleEmailLogin = () => setIsEmailLogin(!isEmailLogin);

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-r from-indigo-500 to-purple-600">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-sm">
        <h2 className="text-2xl font-bold mb-6 text-center">Login to Your Account</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {isEmailLogin ? (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                ref={emailInputRef}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-gray-700 font-semibold">Password</label>
                <button
                  type="button"
                  onClick={goToForgotPassword}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  Forgot password?
                </button>
              </div>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <AnimatedButton
              type="submit"
              loading={loading}
              disabled={loading}
              variant="gradient"
              size="lg"
              fullWidth
            >
              Sign In
            </AnimatedButton>
            <button
              type="button"
              onClick={toggleEmailLogin}
              className="w-full text-center text-indigo-600 hover:text-indigo-800 text-sm"
            >
              Or sign in with Google
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <AnimatedButton
              onClick={handleGoogleLogin}
              loading={loading}
              disabled={loading}
              variant="outline"
              size="lg"
              fullWidth
            >
              <FaGoogle className="mr-2 text-red-500" />
              Sign in with Google
            </AnimatedButton>
            <AnimatedButton
              onClick={toggleEmailLogin}
              variant="outline"
              size="lg"
              fullWidth
            >
              <FaEnvelope className="mr-2 text-indigo-500" />
              Sign in with Email
            </AnimatedButton>
          </div>
        )}

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={goToSignup}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
    </main>
  );
};

export default Login;
